import { useEffect, useRef, useState } from 'react';
import { useEditor } from '../../state/editorStore';
import { api, type StockAudioItem } from '../../api/client';
import { StudioMusicAddDialog } from './StudioMusicAddDialog';
import { playbackClock } from '../../playback/clock';

function secs(ms: number): string {
  return `${(ms / 1000).toFixed(2)}s`;
}

/**
 * Audio tab - original track level + a background-music track (previewed live,
 * ducked under speech). Music files are session-only for now; export mixing is
 * a follow-up (like transitions).
 */
export function StudioAudioPanel() {
  const audio = useEditor((s) => s.audio);
  const setOriginalVolume = useEditor((s) => s.setOriginalVolume);
  const setOriginalMuted = useEditor((s) => s.setOriginalMuted);
  const setMusic = useEditor((s) => s.setMusic);
  const updateMusic = useEditor((s) => s.updateMusic);
  const removeMusic = useEditor((s) => s.removeMusic);
  const durationMs = useEditor((s) => s.durationMs);
  const addSfx = useEditor((s) => s.addSfx);
  const updateSfx = useEditor((s) => s.updateSfx);
  const removeSfx = useEditor((s) => s.removeSfx);
  const fileRef = useRef<HTMLInputElement>(null);
  const sfxFileRef = useRef<HTMLInputElement>(null);
  const [dlgOpen, setDlgOpen] = useState(false);
  const [sfxDlgOpen, setSfxDlgOpen] = useState(false);

  // Add a searched track: server ingests + stores it, we set it as the project's
  // background music with its durable storage key.
  const onPickTrack = async (item: StockAudioItem) => {
    const projectId = useEditor.getState().projectId;
    if (!projectId) {
      setMusic(`${item.title} - ${item.artist}`, item.audioUrl); // demo: session-only
      return;
    }
    const added = await api.musicAdd(projectId, {
      source: item.source,
      audioUrl: item.audioUrl,
      title: item.title,
      artist: item.artist,
      durationSec: item.durationSec,
    });
    setMusic(added.name, added.url);
    updateMusic({ storageKey: added.storageKey });
  };

  const onPick = async (file: File | undefined) => {
    if (!file) return;
    // Play instantly from a local URL, then upload so it gets a durable
    // storageKey and survives refresh (persisted on autosave).
    setMusic(file.name.replace(/\.[^.]+$/, ''), URL.createObjectURL(file));
    const projectId = useEditor.getState().projectId;
    if (!projectId) return; // demo: session-only
    try {
      const { storageKey, url } = await api.brollUpload(projectId, file.name, file.type, file.size);
      await fetch(url, { method: 'PUT', headers: { 'Content-Type': file.type }, body: file });
      updateMusic({ storageKey });
    } catch {
      /* upload failed - the track stays for this session but won't persist */
    }
  };

  // Add a searched sound effect: ingest server-side (durable), place it at the
  // current playhead for a short duration.
  const onPickSfx = async (item: StockAudioItem) => {
    const dur = Math.min(3000, Math.max(400, Math.round((item.durationSec || 2) * 1000)));
    const startMs = Math.round(playbackClock.timeMs());
    const projectId = useEditor.getState().projectId;
    if (!projectId) {
      addSfx({ name: item.title, url: item.audioUrl, startMs, durationMs: dur, volume: 0.9 }); // demo: session-only
      return;
    }
    const added = await api.musicAdd(projectId, {
      source: item.source,
      audioUrl: item.audioUrl,
      title: item.title,
      artist: item.artist,
      durationSec: item.durationSec,
    });
    addSfx({ name: added.name, url: added.url, storageKey: added.storageKey, startMs, durationMs: dur, volume: 0.9 });
  };

  const onPickSfxFile = async (file: File | undefined) => {
    if (!file) return;
    const startMs = Math.round(playbackClock.timeMs());
    const id = addSfx({ name: file.name.replace(/\.[^.]+$/, ''), url: URL.createObjectURL(file), startMs, durationMs: 2000, volume: 0.9 });
    const projectId = useEditor.getState().projectId;
    if (!projectId) return;
    try {
      const { storageKey, url } = await api.brollUpload(projectId, file.name, file.type, file.size);
      await fetch(url, { method: 'PUT', headers: { 'Content-Type': file.type }, body: file });
      updateSfx(id, { storageKey });
    } catch {
      /* upload failed - the clip stays for this session but won't persist */
    }
  };

  const origVol = audio.originalMuted ? 0 : audio.originalVolume;
  const music = audio.music;
  const sfx = audio.sfx ?? [];
  const dur = Math.max(durationMs, 1000);

  return (
    <div className="slp-body shk">

      <span className="str-section">Original sound</span>
      <div className="shk-fields">
        <div className="shk-row">
          <span>Volume</span>
          <div className="shk-size">
            <input type="range" min={0} max={1} step={0.01} value={origVol}
              onChange={(e) => setOriginalVolume(Number(e.target.value))} />
            <span>{Math.round(origVol * 100)}%</span>
          </div>
        </div>
        <div className="shk-row">
          <span>Mute original</span>
          <Toggle on={audio.originalMuted} onClick={() => setOriginalMuted(!audio.originalMuted)} />
        </div>
      </div>

      <StudioAudioEnhance />

      <span className="str-section">Background music</span>
      <input ref={fileRef} type="file" accept="audio/*" hidden
        onChange={(e) => onPick(e.target.files?.[0] ?? undefined)} />

      <StudioMusicAddDialog
        open={dlgOpen}
        onClose={() => setDlgOpen(false)}
        onUpload={() => fileRef.current?.click()}
        onPickTrack={onPickTrack}
      />

      {!music ? (
        <button className="sau-add" onClick={() => setDlgOpen(true)}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <path d="M9 18V5l10-2v13M9 18a3 3 0 1 1-6 0 3 3 0 0 1 6 0Zm10-2a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
          </svg>
          Add a music track
        </button>
      ) : (
        <div className="sau-track">
          <div className="sau-track-head">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <path d="M9 18V5l10-2v13M9 18a3 3 0 1 1-6 0 3 3 0 0 1 6 0Zm10-2a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
            </svg>
            <span className="sau-track-name" title={music.name}>{music.name}</span>
            <button className="str-remove" onClick={removeMusic}>Remove</button>
          </div>
          <div className="shk-fields">
            <div className="shk-row">
              <span>Volume</span>
              <div className="shk-size">
                <input type="range" min={0} max={1} step={0.01} value={music.volume}
                  onChange={(e) => updateMusic({ volume: Number(e.target.value) })} />
                <span>{Math.round(music.volume * 100)}%</span>
              </div>
            </div>
            <div className="shk-row">
              <span>Duck under speech</span>
              <Toggle on={music.duck} onClick={() => updateMusic({ duck: !music.duck })} />
            </div>
            <div className="shk-row">
              <span>Loop</span>
              <Toggle on={music.loop} onClick={() => updateMusic({ loop: !music.loop })} />
            </div>
            <button className="sau-replace" onClick={() => setDlgOpen(true)}>Replace track</button>
          </div>
        </div>
      )}

      <span className="str-section">Sound effects{sfx.length ? ` · ${sfx.length}` : ''}</span>
      <input ref={sfxFileRef} type="file" accept="audio/*" hidden
        onChange={(e) => onPickSfxFile(e.target.files?.[0] ?? undefined)} />
      <StudioMusicAddDialog
        open={sfxDlgOpen}
        onClose={() => setSfxDlgOpen(false)}
        onUpload={() => sfxFileRef.current?.click()}
        onPickTrack={onPickSfx}
      />
      <button className="sau-add" onClick={() => setSfxDlgOpen(true)}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <path d="M11 5 6 9H2v6h4l5 4zM15.5 8.5a5 5 0 0 1 0 7M19 5a9 9 0 0 1 0 14" />
        </svg>
        Add sound effect
      </button>
      {sfx.length === 0 ? (
        <div className="str-empty">No sound effects yet. Add one, then set when it plays.</div>
      ) : (
        <div className="str-list">
          {sfx.map((c) => (
            <div key={c.id} className="sau-track">
              <div className="sau-track-head">
                <span className="sau-track-name" title={c.name}>{c.name}</span>
                <button className="str-remove" onClick={() => removeSfx(c.id)}>Remove</button>
              </div>
              <div className="shk-fields">
                <div className="shk-row">
                  <span>Start</span>
                  <div className="shk-size">
                    <input type="range" min={0} max={dur} step={50} value={Math.min(c.startMs, dur)}
                      onChange={(e) => updateSfx(c.id, { startMs: Number(e.target.value) })} />
                    <span>{secs(c.startMs)}</span>
                  </div>
                </div>
                <div className="shk-row">
                  <span>Duration</span>
                  <div className="shk-size">
                    <input type="range" min={200} max={5000} step={50} value={c.durationMs}
                      onChange={(e) => updateSfx(c.id, { durationMs: Number(e.target.value) })} />
                    <span>{secs(c.durationMs)}</span>
                  </div>
                </div>
                <div className="shk-row">
                  <span>Volume</span>
                  <div className="shk-size">
                    <input type="range" min={0} max={1} step={0.01} value={c.volume}
                      onChange={(e) => updateSfx(c.id, { volume: Number(e.target.value) })} />
                    <span>{Math.round(c.volume * 100)}%</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * "Studio sound" enhancer - a one-click chain (denoise + EQ + compression +
 * loudness) that makes phone/room audio sound recorded in a booth. Runs as a
 * background job (like the depth cut-out); once ready you A/B it against the raw
 * track live, and the export swaps it in when the toggle is on.
 */
function StudioAudioEnhance() {
  const enh = useEditor((s) => s.audioEnhance);
  const setAudioEnhance = useEditor((s) => s.setAudioEnhance);
  const [busy, setBusy] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopPoll = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = null;
  };

  // Load the saved enhance state when the tab opens (status, params, A/B, url).
  useEffect(() => {
    const projectId = useEditor.getState().projectId;
    if (!projectId) return;
    let alive = true;
    api
      .getAudioEnhance(projectId)
      .then((r) => {
        if (!alive) return;
        setAudioEnhance({ status: r.status, progress: r.progress, enabled: r.enabled, strength: r.strength, loudnorm: r.loudnorm, url: r.url, stale: r.stale, error: r.error });
        if (r.status === 'running') poll(projectId);
      })
      .catch(() => undefined);
    return () => {
      alive = false;
      stopPoll();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const poll = (projectId: string) => {
    stopPoll();
    pollRef.current = setInterval(async () => {
      try {
        const r = await api.getAudioEnhance(projectId);
        setAudioEnhance({ status: r.status, progress: r.progress, enabled: r.enabled, strength: r.strength, loudnorm: r.loudnorm, url: r.url, stale: r.stale, error: r.error });
        if (r.status !== 'running') {
          stopPoll();
          setBusy(false);
        }
      } catch {
        /* transient - keep polling */
      }
    }, 1500);
  };

  const run = async () => {
    const projectId = useEditor.getState().projectId;
    if (!projectId || busy) return;
    setBusy(true);
    setAudioEnhance({ status: 'running', progress: 0, enabled: true, error: null });
    try {
      await api.startAudioEnhance(projectId, { strength: enh.strength, loudnorm: enh.loudnorm });
      poll(projectId);
    } catch {
      setAudioEnhance({ status: 'failed', error: 'Could not start enhancer.' });
      setBusy(false);
    }
  };

  const toggleEnabled = async (on: boolean) => {
    const projectId = useEditor.getState().projectId;
    setAudioEnhance({ enabled: on });
    if (projectId) await api.setAudioEnhanceEnabled(projectId, on).catch(() => undefined);
  };

  const running = enh.status === 'running';
  const ready = enh.status === 'ready';
  const LOUDNESS = [
    { v: -16, label: 'Podcast (-16)' },
    { v: -14, label: 'Social (-14)' },
    { v: -12, label: 'Punchy (-12)' },
  ];

  return (
    <>
      <span className="str-section">Enhance audio</span>
      <div className="shk-fields">
        <div className="shk-row">
          <span>Noise reduction</span>
          <div className="shk-size">
            <input
              type="range"
              min={0}
              max={1}
              step={0.05}
              value={enh.strength}
              disabled={running}
              onChange={(e) => setAudioEnhance({ strength: Number(e.target.value) })}
            />
            <span>{Math.round(enh.strength * 100)}%</span>
          </div>
        </div>
        <div className="shk-row">
          <span>Loudness</span>
          <select
            className="si-select"
            style={{ color: 'var(--stext)' }}
            value={enh.loudnorm}
            disabled={running}
            onChange={(e) => setAudioEnhance({ loudnorm: Number(e.target.value) })}
          >
            {LOUDNESS.map((o) => (
              <option key={o.v} value={o.v}>
                {o.label}
              </option>
            ))}
          </select>
        </div>

        {ready && (
          <div className="shk-row">
            <span>Use enhanced audio</span>
            <Toggle on={enh.enabled} onClick={() => void toggleEnabled(!enh.enabled)} />
          </div>
        )}

        <button className="sau-add" onClick={() => void run()} disabled={running || busy}>
          {running ? `Enhancing… ${Math.round((enh.progress ?? 0) * 100)}%` : ready ? (enh.stale ? 'Re-enhance (settings changed)' : 'Re-enhance') : 'Enhance audio'}
        </button>

        {enh.status === 'failed' && <div className="str-empty">{enh.error ?? 'Enhancement failed.'}</div>}
      </div>
    </>
  );
}

function Toggle({ on, onClick }: { on: boolean; onClick: () => void }) {
  return (
    <button className={on ? 'shk-toggle on' : 'shk-toggle'} onClick={onClick} aria-pressed={on}>
      <span className="shk-toggle-knob" />
    </button>
  );
}
